// CB:BEGIN payment-api-helper
import { getApiUrl } from './getApiUrl';

export interface PaymentProfile {
  hasValidPayment: boolean;
  defaultMethodType: string | null;
  card: {
    id: string | null;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  bank: {
    id: string | null;
    name: string;
    last4: string;
    routingLast4: string;
  } | null;
}

export interface SetupIntentResponse {
  clientSecret: string;
}

class PaymentApi {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const clerk = (window as any).Clerk;
    if (!clerk?.session) {
      throw new Error('Not authenticated');
    }
    const token = await clerk.session.getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async ensureCustomer(): Promise<{ customerId: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payment/ensure-customer'), {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to ensure customer' }));
      throw new Error(error.error || 'Failed to ensure customer');
    }
    return response.json();
  }

  async getPaymentProfile(): Promise<PaymentProfile> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payment/profile'), {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to load payment profile');
    }
    return response.json();
  }

  async createSetupIntent(type: 'card' | 'us_bank_account'): Promise<SetupIntentResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payment/create-setup-intent'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ type }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create setup intent');
    }
    return response.json();
  }

  async attachPaymentMethod(paymentMethodId: string, setDefault: boolean = false): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payment/attach-method'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ paymentMethodId, setDefault }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to attach payment method');
    }
  }

  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl(`/api/payment/method/${paymentMethodId}`), {
      method: 'DELETE',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove payment method');
    }
  }

  async seedDemoPayment(): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payment/demo-seed'), {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to seed demo payment');
    }
  }

  /**
   * Create or reuse PaymentIntent for escrow funding (Option A).
   * Uses escrow as source of truth; includes auth. Pass projectId or escrowId.
   */
  /**
   * Clear a stale PaymentIntent from the homeowner's escrow.
   * Called when confirmCardPayment returns "No such payment_intent" — clears the stale PI
   * so the next createPaymentIntentForEscrow call creates a fresh one.
   */
  async clearStaleIntent(params: { projectId?: string; escrowId?: string }): Promise<void> {
    const headers = await this.getAuthHeaders();
    await fetch(getApiUrl('/api/payments/clear-stale-intent'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(params),
    });
    // best-effort — don't throw if it fails
  }

  async createPaymentIntentForEscrow(params: { projectId?: string; escrowId?: string }): Promise<{
    clientSecret: string | null;
    stripePaymentIntentId: string;
    escrowId?: string;
    bidAmountCents: number;
    platformFeeCents: number;
    totalAmountCents: number;
    alreadyPaid?: boolean;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/payments/create-payment-intent'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(params),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create payment intent');
    }
    return data;
  }
}

export const paymentApi = new PaymentApi();
// CB:END payment-api-helper

